import React, { Suspense, useEffect, useMemo } from 'react';
import { createBrowserRouter } from 'react-router-dom';
import { PagePath } from '@monorepo/client-types';
import { useFeatureFlagContext } from '@monorepo/feature-flag/client';
import { createPublicCatchAllLoader } from '@monorepo/hub-common';
import { AppLoadingSpinner } from '@monorepo/react-components';
import ErrorPage from '../../features/error-page/error-page';
import LogoutPage from '../../features/logout/logout';
import QuickPurchasePage from '../../features/quick-purchase/quick-purchase-page';
import { getRoot } from '../../helpers/getters.store';
import {
  Article,
  LayoutNotAuth,
  Login,
  OtpLoginPage,
  PublicTradeTalk,
  Register,
  TradeTalks,
} from '../../routers/lazy-routes/lazy-public-components';
import { EnvService } from '../../services/core/env-service';

const { application } = EnvService;

// Helper function to wrap components with Suspense
const withSuspense = (Component: React.FC) => (
  <Suspense fallback={<AppLoadingSpinner application={application} />}>
    <Component />
  </Suspense>
);

const privateRoutePaths = [
  PagePath.Overview,
  PagePath.Orders,
  PagePath.Contests,
  PagePath.Profile,
  PagePath.Affiliation,
  PagePath.Achievements,
  PagePath.Playbook,
  PagePath.Partners,
  PagePath.NewAccount,
  PagePath.NewAddon,
  PagePath.Autochartist,
  PagePath.AssetsSpecifications,
  PagePath.HubDownloads,
  PagePath.HubVod,
  PagePath.TradingPlan,
  PagePath.AffiliateKit,
  PagePath.PerformanceCoach,
  PagePath.AssetsManagement,
  PagePath.Position,
  PagePath.Charts,
  PagePath.Purchase,
  PagePath.AfterPurchase,
  PagePath.Dashboard,
  PagePath.Welcome,
  PagePath.PrivacyOptOut,
  // Derive /purchases from PagePath.Coupons to match all /purchases/* routes
  PagePath.Coupons.split('/').slice(0, 2).join('/'),
];

// Generate patterns with trailing slashes for dynamic routes
// This matches both exact paths and paths with sub-routes (e.g., /overview and /overview/123)
const privateRoutePatterns = [
  ...privateRoutePaths,
  ...privateRoutePaths.map((path) => `${path}/`),
];

const isPrivateRoute = (pathname: string): boolean =>
  privateRoutePatterns.some((pattern) => pathname.startsWith(pattern));

const catchAllLoader = createPublicCatchAllLoader(
  isPrivateRoute,
  () => getRoot().dataStore.userStore.isLoggedIn,
  '/',
);

export const usePublicRoutes = (basename: string) => {
  const featureFlag = useFeatureFlagContext();

  //access query params natively without react hooks
  const debugMode = useMemo(() => {
    const queryParams = new URLSearchParams(window.location.search);
    return queryParams.get('debugMode') === 'true';
  }, [window.location.search]);

  useEffect(() => {
    featureFlag.setAttributes({
      ...featureFlag.getAttributes(), // Preserve existing attributes like environment
      debugMode,
    });
  }, [debugMode]);

  return createBrowserRouter(
    [
      {
        path: '/logout',
        element: <LogoutPage />,
      },
      {
        path: '/',
        element: withSuspense(LayoutNotAuth),
        errorElement: <ErrorPage />,
        children: [
          { index: true, element: <Login /> },
          { path: 'register', element: withSuspense(Register) },
          {
            path: 'otp-login',
            element: withSuspense(OtpLoginPage),
          },
          {
            path: '*',
            loader: catchAllLoader,
          },
        ],
      },
      {
        path: '/quick-purchase',
        element: <QuickPurchasePage />,
        errorElement: <ErrorPage />,
      },
      {
        path: PagePath.TradeTalk,
        element: withSuspense(PublicTradeTalk),
        errorElement: <ErrorPage />,
        children: [
          { index: true, element: withSuspense(TradeTalks) },
          {
            path: ':id',
            element: withSuspense(() => <Article isPublic />),
          },
        ],
      },
    ],
    {
      basename: basename,
    },
  );
};
